import { useState, useMemo } from 'react';
import {
  useMonitors,
  useMonitorStats,
  useCreateMonitor,
  useDeleteMonitor,
  useForceCheck,
  useUpdateMonitor,
  type Monitor,
  type MonitorType,
  type MonitorCreateRequest,
} from '../api/monitoring';

// ── Constants ──

const MONITOR_TYPE_CONFIG: Record<MonitorType, { label: string; color: string; iconPath: string }> = {
  domain_watchdog: {
    label: 'Domain',
    color: 'cyan',
    // Globe icon
    iconPath: 'M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5a17.92 17.92 0 01-8.716-2.247m0 0A9.015 9.015 0 003 12c0-1.605.42-3.113 1.157-4.418',
  },
  person_watchdog: {
    label: 'Person',
    color: 'purple',
    // User icon
    iconPath: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z',
  },
  ip_watchdog: {
    label: 'IP',
    color: 'blue',
    // Server icon
    iconPath: 'M21.75 17.25v-.228a4.5 4.5 0 00-.12-1.03l-2.268-9.64a3.375 3.375 0 00-3.285-2.602H7.923a3.375 3.375 0 00-3.285 2.602l-2.268 9.64a4.5 4.5 0 00-.12 1.03v.228m19.5 0a3 3 0 01-3 3H5.25a3 3 0 01-3-3m19.5 0a3 3 0 00-3-3H5.25a3 3 0 00-3 3m16.5 0h.008v.008h-.008v-.008zm-3 0h.008v.008h-.008v-.008z',
  },
  keyword_watchdog: {
    label: 'Keyword',
    color: 'amber',
    // Search icon
    iconPath: 'M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z',
  },
  dark_web_watchdog: {
    label: 'Dark Web',
    color: 'red',
    // Shield icon
    iconPath: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z',
  },
};

const STATUS_STYLES: Record<string, { dot: string; badge: string; label: string }> = {
  active: { dot: 'bg-green-500', badge: 'bg-green-500/15 text-green-400 border-green-500/30', label: 'Active' },
  paused: { dot: 'bg-yellow-500', badge: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30', label: 'Paused' },
  alert: { dot: 'bg-orange-500', badge: 'bg-orange-500/15 text-orange-400 border-orange-500/30', label: 'Alert' },
  error: { dot: 'bg-red-500', badge: 'bg-red-500/15 text-red-400 border-red-500/30', label: 'Error' },
};

const COLOR_CLASSES: Record<string, string> = {
  cyan: 'border-cyan-500/30 text-cyan-400',
  purple: 'border-purple-500/30 text-purple-400',
  blue: 'border-blue-500/30 text-blue-400',
  amber: 'border-amber-500/30 text-amber-400',
  red: 'border-red-500/30 text-red-400',
  green: 'border-green-500/30 text-green-400',
  emerald: 'border-emerald-500/30 text-emerald-400',
};

const INTERVAL_OPTIONS = [
  { value: 5, label: '5 min' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hour' },
  { value: 360, label: '6 hours' },
  { value: 1440, label: '24 hours' },
];

const DATA_SOURCES = [
  { name: 'DNS / WHOIS Lookup', types: ['domain_watchdog'], url: 'https://who.is' },
  { name: 'SSL Certificate Transparency', types: ['domain_watchdog'], url: 'https://crt.sh' },
  { name: 'Shodan', types: ['ip_watchdog', 'domain_watchdog'], url: 'https://www.shodan.io' },
  { name: 'Have I Been Pwned', types: ['person_watchdog', 'dark_web_watchdog'], url: 'https://haveibeenpwned.com' },
  { name: 'Social Media APIs', types: ['person_watchdog', 'keyword_watchdog'], url: null },
  { name: 'Google Dorking', types: ['keyword_watchdog', 'person_watchdog'], url: null },
  { name: 'Tor Hidden Services', types: ['dark_web_watchdog'], url: null },
  { name: 'Paste Site Monitors', types: ['dark_web_watchdog', 'keyword_watchdog'], url: 'https://pastebin.com' },
  { name: 'AbuseIPDB', types: ['ip_watchdog'], url: 'https://www.abuseipdb.com' },
  { name: 'VirusTotal', types: ['ip_watchdog', 'domain_watchdog'], url: 'https://www.virustotal.com' },
];

// ── Helper functions ──

function formatInterval(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${minutes / 60}h`;
  return `${minutes / 1440}d`;
}

function formatTimeAgo(isoString: string | null): string {
  if (!isoString) return 'Never';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatTimeUntil(isoString: string | null): string {
  if (!isoString) return '--';
  const diff = new Date(isoString).getTime() - Date.now();
  if (diff < 0) return 'Overdue';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

// ── Main Page Component ──

export default function MonitoringPage() {
  const { data: monitorsData, isLoading: monitorsLoading } = useMonitors();
  const { data: stats, isLoading: statsLoading } = useMonitorStats();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedMonitorId, setSelectedMonitorId] = useState<string | null>(null);

  const monitors = monitorsData?.monitors ?? [];

  const filteredMonitors = useMemo(() => {
    let result = monitors;
    if (filterType !== 'all') {
      result = result.filter((m) => m.monitor_type === filterType);
    }
    if (filterStatus !== 'all') {
      if (filterStatus === 'paused') {
        result = result.filter((m) => !m.is_active);
      } else {
        result = result.filter((m) => m.is_active && m.status === filterStatus);
      }
    }
    return result;
  }, [monitors, filterType, filterStatus]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Continuous Monitoring</h1>
          <p className="text-sm text-gray-400 mt-1">
            Automated watchdogs for domains, persons, IPs, keywords & dark web
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white font-medium text-sm hover:opacity-90 transition-opacity flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          New Monitor
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatsCard
          label="Active Monitors"
          value={statsLoading ? '--' : String(stats?.active_monitors ?? 0)}
          color="green"
        />
        <StatsCard
          label="Total Checks"
          value={statsLoading ? '--' : String(stats?.total_checks ?? 0)}
          color="cyan"
        />
        <StatsCard
          label="Alerts Triggered"
          value={statsLoading ? '--' : String(stats?.total_alerts ?? 0)}
          color="amber"
        />
        <StatsCard
          label="Uptime"
          value={statsLoading ? '--' : `${stats?.uptime_percentage ?? 0}%`}
          color="emerald"
        />
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <CreateMonitorForm onClose={() => setShowCreateForm(false)} />
      )}

      {/* Filters */}
      <div className="bg-surface-secondary rounded-xl border border-border-subtle p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 uppercase font-medium">Type:</span>
            <div className="flex gap-1">
              <FilterButton active={filterType === 'all'} onClick={() => setFilterType('all')}>All</FilterButton>
              {Object.entries(MONITOR_TYPE_CONFIG).map(([type, config]) => (
                <FilterButton
                  key={type}
                  active={filterType === type}
                  onClick={() => setFilterType(type)}
                >
                  {config.label}
                </FilterButton>
              ))}
            </div>
          </div>
          <div className="h-4 w-px bg-border-subtle" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 uppercase font-medium">Status:</span>
            <div className="flex gap-1">
              <FilterButton active={filterStatus === 'all'} onClick={() => setFilterStatus('all')}>All</FilterButton>
              <FilterButton active={filterStatus === 'active'} onClick={() => setFilterStatus('active')}>Active</FilterButton>
              <FilterButton active={filterStatus === 'paused'} onClick={() => setFilterStatus('paused')}>Paused</FilterButton>
              <FilterButton active={filterStatus === 'alert'} onClick={() => setFilterStatus('alert')}>Alert</FilterButton>
              <FilterButton active={filterStatus === 'error'} onClick={() => setFilterStatus('error')}>Error</FilterButton>
            </div>
          </div>
          <div className="ml-auto text-xs text-gray-500">
            {filteredMonitors.length} monitor{filteredMonitors.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Monitors Grid */}
      {monitorsLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner />
          <span className="ml-3 text-sm text-gray-400">Loading monitors...</span>
        </div>
      ) : filteredMonitors.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-surface-secondary rounded-xl border border-border-subtle">
          <svg className="w-12 h-12 text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <p className="text-sm text-gray-400">No monitors found</p>
          <p className="text-xs text-gray-500 mt-1">Create your first monitor to start continuous surveillance</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredMonitors.map((monitor) => (
            <MonitorCard
              key={monitor.id}
              monitor={monitor}
              isSelected={selectedMonitorId === monitor.id}
              onSelect={() => setSelectedMonitorId(selectedMonitorId === monitor.id ? null : monitor.id)}
            />
          ))}
        </div>
      )}

      {/* Source Links */}
      <SourceLinks />
    </div>
  );
}

// ── Stats Card ──

function StatsCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className={`bg-surface-secondary rounded-xl border p-4 ${COLOR_CLASSES[color] || 'border-border-subtle text-gray-400'}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-bold mt-1">{value}</div>
    </div>
  );
}

// ── Filter Button ──

function FilterButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
        active
          ? 'bg-helios-700/30 border border-cyber-cyan/40 text-gray-100'
          : 'text-gray-400 hover:text-gray-200 border border-transparent hover:border-border-subtle'
      }`}
    >
      {children}
    </button>
  );
}

// ── Monitor Card ──

function MonitorCard({
  monitor,
  isSelected,
  onSelect,
}: {
  monitor: Monitor;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const forceCheck = useForceCheck();
  const updateMonitor = useUpdateMonitor();
  const deleteMonitor = useDeleteMonitor();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const typeConfig = MONITOR_TYPE_CONFIG[monitor.monitor_type] || {
    label: monitor.monitor_type,
    color: 'cyan',
    iconPath: '',
  };
  const statusStyle = STATUS_STYLES[monitor.is_active ? (monitor.status || 'active') : 'paused'] || STATUS_STYLES.active;

  const lastResultAlerts = (monitor.last_result?.alerts as string[]) || [];

  function handleTogglePause() {
    updateMonitor.mutate({
      monitorId: monitor.id,
      is_active: !monitor.is_active,
    });
  }

  function handleForceCheck() {
    forceCheck.mutate(monitor.id);
  }

  function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 3000);
      return;
    }
    deleteMonitor.mutate(monitor.id);
  }

  return (
    <div
      className={`bg-surface-secondary rounded-xl border transition-all ${
        isSelected ? 'border-cyber-cyan/50 shadow-lg shadow-cyber-cyan/5' : 'border-border-subtle hover:border-border-subtle/80'
      }`}
    >
      {/* Card Header */}
      <div className="p-4 cursor-pointer" onClick={onSelect}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {/* Type Icon */}
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-${typeConfig.color}-500/10 border border-${typeConfig.color}-500/20`}>
              <svg
                className={`w-4.5 h-4.5 text-${typeConfig.color}-400`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d={typeConfig.iconPath} />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-200 leading-tight">{monitor.name}</h3>
              <p className="text-xs text-gray-500 mt-0.5 font-mono">{monitor.target}</p>
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${statusStyle.dot} ${monitor.is_active && monitor.status !== 'error' ? 'animate-pulse' : ''}`} />
            <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyle.badge}`}>
              {statusStyle.label}
            </span>
          </div>
        </div>

        {/* Monitor Metadata */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-surface-tertiary rounded-lg px-2.5 py-1.5">
            <div className="text-[10px] text-gray-600 uppercase">Last Check</div>
            <div className="text-xs text-gray-300 font-medium">{formatTimeAgo(monitor.last_check)}</div>
          </div>
          <div className="bg-surface-tertiary rounded-lg px-2.5 py-1.5">
            <div className="text-[10px] text-gray-600 uppercase">Next Check</div>
            <div className="text-xs text-gray-300 font-medium">
              {monitor.is_active ? formatTimeUntil(monitor.next_check) : '--'}
            </div>
          </div>
          <div className="bg-surface-tertiary rounded-lg px-2.5 py-1.5">
            <div className="text-[10px] text-gray-600 uppercase">Interval</div>
            <div className="text-xs text-gray-300 font-medium">{formatInterval(monitor.interval_minutes)}</div>
          </div>
        </div>

        {/* Counters Row */}
        <div className="flex items-center gap-4 text-xs">
          <span className="text-gray-500">
            Checks: <span className="text-gray-300 font-medium">{monitor.checks_count}</span>
          </span>
          <span className="text-gray-500">
            Alerts: <span className={`font-medium ${monitor.alerts_count > 0 ? 'text-amber-400' : 'text-gray-300'}`}>
              {monitor.alerts_count}
            </span>
          </span>
          {monitor.alerts_enabled && (
            <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-500 border border-green-500/20">
              Alerts ON
            </span>
          )}
        </div>

        {/* Recent Alerts Preview */}
        {lastResultAlerts.length > 0 && (
          <div className="mt-3 space-y-1">
            {lastResultAlerts.slice(0, 2).map((alert, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-xs text-amber-400 bg-amber-500/5 rounded-lg px-2.5 py-1.5 border border-amber-500/10"
              >
                <svg className="w-3 h-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <span>{alert}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center border-t border-border-subtle">
        <button
          onClick={handleTogglePause}
          disabled={updateMonitor.isPending}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50"
          title={monitor.is_active ? 'Pause monitor' : 'Resume monitor'}
        >
          {monitor.is_active ? (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" />
              </svg>
              Pause
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
              </svg>
              Resume
            </>
          )}
        </button>

        <div className="w-px h-5 bg-border-subtle" />

        <button
          onClick={handleForceCheck}
          disabled={forceCheck.isPending || !monitor.is_active}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs text-gray-400 hover:text-cyber-cyan transition-colors disabled:opacity-50"
          title="Force immediate check"
        >
          {forceCheck.isPending ? (
            <Spinner />
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
            </svg>
          )}
          Check Now
        </button>

        <div className="w-px h-5 bg-border-subtle" />

        <button
          onClick={handleDelete}
          disabled={deleteMonitor.isPending}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs transition-colors disabled:opacity-50 ${
            confirmDelete
              ? 'text-red-400 bg-red-500/10'
              : 'text-gray-400 hover:text-red-400'
          }`}
          title={confirmDelete ? 'Click again to confirm deletion' : 'Delete monitor'}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
          {confirmDelete ? 'Confirm' : 'Delete'}
        </button>
      </div>
    </div>
  );
}

// ── Create Monitor Form ──

function CreateMonitorForm({ onClose }: { onClose: () => void }) {
  const createMonitor = useCreateMonitor();
  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const [monitorType, setMonitorType] = useState<MonitorType>('domain_watchdog');
  const [interval, setInterval] = useState(60);
  const [alertsEnabled, setAlertsEnabled] = useState(true);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !target.trim()) return;

    const request: MonitorCreateRequest = {
      name: name.trim(),
      target: target.trim(),
      monitor_type: monitorType,
      interval_minutes: interval,
      alerts_enabled: alertsEnabled,
    };

    createMonitor.mutate(request, {
      onSuccess: () => onClose(),
    });
  }

  const selectedTypeConfig = MONITOR_TYPE_CONFIG[monitorType];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-surface-secondary border border-border-subtle rounded-2xl w-full max-w-lg mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b border-border-subtle">
          <div>
            <h2 className="text-lg font-bold text-gray-100">New Monitor</h2>
            <p className="text-xs text-gray-500 mt-0.5">Configure a new continuous watchdog</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-surface-tertiary transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Monitor Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Mowibo Domain Watch"
              className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-4 py-2.5 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
              autoFocus
            />
          </div>

          {/* Target */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Target *</label>
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder={
                monitorType === 'domain_watchdog' ? 'e.g. mowibo.com' :
                monitorType === 'person_watchdog' ? 'e.g. Jean Dupont' :
                monitorType === 'ip_watchdog' ? 'e.g. 185.12.45.78' :
                monitorType === 'keyword_watchdog' ? 'e.g. mowibo' :
                'e.g. mowibo.com'
              }
              className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-4 py-2.5 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
            />
          </div>

          {/* Monitor Type */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Monitor Type</label>
            <div className="grid grid-cols-5 gap-2">
              {(Object.entries(MONITOR_TYPE_CONFIG) as [MonitorType, typeof MONITOR_TYPE_CONFIG[MonitorType]][]).map(
                ([type, config]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setMonitorType(type)}
                    className={`flex flex-col items-center gap-1.5 px-2 py-3 rounded-lg border transition-all text-center ${
                      monitorType === type
                        ? 'bg-helios-700/30 border-cyber-cyan/40 text-gray-100'
                        : 'bg-surface-tertiary border-border-subtle text-gray-400 hover:text-gray-200 hover:border-border-subtle/80'
                    }`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d={config.iconPath} />
                    </svg>
                    <span className="text-[10px] leading-tight">{config.label}</span>
                  </button>
                ),
              )}
            </div>
            <p className="text-[10px] text-gray-600 mt-1.5">
              {selectedTypeConfig.label} watchdog selected
            </p>
          </div>

          {/* Interval */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Check Interval</label>
            <div className="grid grid-cols-6 gap-2">
              {INTERVAL_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setInterval(opt.value)}
                  className={`px-2 py-2 rounded-lg border transition-all text-xs ${
                    interval === opt.value
                      ? 'bg-helios-700/30 border-cyber-cyan/40 text-gray-100'
                      : 'bg-surface-tertiary border-border-subtle text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Alerts Toggle */}
          <div className="flex items-center justify-between p-3 bg-surface-tertiary rounded-lg border border-border-subtle">
            <div>
              <span className="text-sm text-gray-200">Enable Alerts</span>
              <p className="text-xs text-gray-500 mt-0.5">Get notified when changes or threats are detected</p>
            </div>
            <button
              type="button"
              onClick={() => setAlertsEnabled(!alertsEnabled)}
              className={`relative w-11 h-6 rounded-full transition-colors ${
                alertsEnabled ? 'bg-cyber-cyan' : 'bg-gray-600'
              }`}
            >
              <div
                className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  alertsEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {/* Error */}
          {createMonitor.isError && (
            <p className="text-xs text-red-400">
              Error: {(createMonitor.error as Error).message}
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg border border-border-subtle text-gray-400 text-sm hover:text-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMonitor.isPending || !name.trim() || !target.trim()}
              className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {createMonitor.isPending ? (
                <>
                  <Spinner /> Creating...
                </>
              ) : (
                'Create Monitor'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Source Links ──

function SourceLinks() {
  return (
    <div className="bg-surface-secondary rounded-xl border border-border-subtle p-5">
      <h3 className="text-sm font-semibold text-gray-300 mb-3">Monitoring Data Sources</h3>
      <p className="text-xs text-gray-500 mb-4">
        Continuous monitoring aggregates intelligence from multiple OSINT and threat intelligence sources.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {DATA_SOURCES.map((source) => (
          <div
            key={source.name}
            className="flex items-center justify-between p-2.5 bg-surface-tertiary rounded-lg border border-border-subtle"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
              <span className="text-xs text-gray-300 truncate">{source.name}</span>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
              {source.types.slice(0, 2).map((t) => {
                const config = MONITOR_TYPE_CONFIG[t as MonitorType];
                return config ? (
                  <span
                    key={t}
                    className={`text-[9px] px-1 py-0.5 rounded border bg-${config.color}-500/10 text-${config.color}-400 border-${config.color}-500/20`}
                  >
                    {config.label}
                  </span>
                ) : null;
              })}
              {source.url && (
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-cyber-cyan transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                  </svg>
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Spinner ──

function Spinner() {
  return (
    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}
